"use client";

/**
 * P2-06j · Newsletter signup form (client) — shared by the homepage block and
 * the footer slot (P2-01). Server-authoritative submit via
 * `newsletter.subscribe`; the client pre-validates the email and enforces the
 * consent checkbox for a fast inline error, but the server re-validates and
 * owns dedupe/rate-limit. A hidden honeypot catches trivial bots. States: idle,
 * submitting (disabled + spinner), success (config message replaces the form),
 * error (inline, retryable). `tone="inverse"` themes it for the dark footer.
 */
import { useEffect, useId, useRef, useState } from "react";

import { cn } from "~/lib/utils";
import { api } from "~/trpc/react";
import type { NewsletterSource } from "~/server/content/blocks/newsletter";
import { useTranslator } from "~/i18n/provider";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface NewsletterFormProps {
  source: NewsletterSource;
  placeholder: string;
  ctaLabel: string;
  consentText?: string;
  successMessage: string;
  layout: "inline" | "stacked";
  tone?: "default" | "inverse";
}

export function NewsletterForm({
  source,
  placeholder,
  ctaLabel,
  consentText,
  successMessage,
  layout,
  tone = "default",
}: NewsletterFormProps) {
  const t = useTranslator();
  const uid = useId();
  const emailId = `${uid}-email`;
  const errorId = `${uid}-error`;

  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [clientError, setClientError] = useState<string | null>(null);

  const subscribe = api.newsletter.subscribe.useMutation();
  const inverse = tone === "inverse";

  // On success the form unmounts and this message mounts in its place — move
  // focus to it so keyboard focus isn't dropped to <body>, and so the status
  // is reliably announced (a freshly-mounted live region often isn't).
  const successRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (subscribe.isSuccess) successRef.current?.focus();
  }, [subscribe.isSuccess]);

  if (subscribe.isSuccess) {
    return (
      <p
        ref={successRef}
        role="status"
        aria-live="polite"
        tabIndex={-1}
        className={cn(
          "text-sm font-medium outline-none",
          inverse ? "text-on-surface-inverse" : "text-fg",
        )}
      >
        {/* P15-01 · Under double opt-in the honest state is "check your
            inbox", not "you're on the list" — the server says which applies,
            so the operator's configured message shows only when it is true. */}
        {subscribe.data?.confirmationPending
          ? t("blocks.newsletterConfirmPending")
          : successMessage}
      </p>
    );
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setClientError(null);
    if (!EMAIL_RE.test(email)) {
      setClientError("Please enter a valid email address.");
      return;
    }
    if (consentText && !consent) {
      setClientError("Please agree before subscribing.");
      return;
    }
    subscribe.mutate({
      email,
      source,
      website,
      consent: consentText ? consent : undefined,
    });
  };

  const message = clientError
    ? clientError
    : subscribe.isError
      ? "Something went wrong. Please try again."
      : "";
  const describedBy = message ? errorId : undefined;

  const inputClass = cn(
    "min-w-0 flex-1 rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
    inverse
      ? "min-h-12 border-border-inverse bg-white/5 text-on-surface-inverse placeholder:text-on-surface-inverse-muted"
      : "border-border bg-surface text-fg placeholder:text-fg-muted",
    clientError && "border-danger",
  );
  const buttonClass = cn(
    "inline-flex shrink-0 items-center justify-center gap-2 rounded-[var(--btn-radius)] px-4 py-2 text-sm [font-weight:var(--btn-weight)] transition disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2",
    inverse
      ? "min-h-12 bg-on-surface-inverse px-6 text-surface-inverse hover:opacity-90 focus-visible:ring-offset-[var(--color-surface-inverse)]"
      : "bg-primary text-primary-fg hover:brightness-95 focus-visible:ring-offset-bg",
  );

  return (
    <form
      aria-label={t("blocks.newsletterSignup")}
      onSubmit={onSubmit}
      noValidate
      className={cn(
        "relative w-full",
        layout === "stacked" ? "max-w-md" : "max-w-xl",
      )}
    >
      {/* Both layouts stack on mobile; inline goes side-by-side from sm up. */}
      <div
        className={cn(
          "flex flex-col gap-2",
          layout === "inline" && "sm:flex-row",
        )}
      >
        <label htmlFor={emailId} className="sr-only">
          Email address
        </label>
        <input
          id={emailId}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          autoComplete="email"
          disabled={subscribe.isPending}
          aria-invalid={clientError ? true : undefined}
          aria-describedby={describedBy}
          className={inputClass}
        />
        {/* Honeypot: off-screen, non-tabbable; a filled value marks a bot. */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          className="absolute left-[-9999px] top-0 h-0 w-0 opacity-0"
        />
        <button
          type="submit"
          disabled={subscribe.isPending}
          aria-busy={subscribe.isPending || undefined}
          className={buttonClass}
        >
          {subscribe.isPending && (
            <span
              aria-hidden
              className="size-3.5 animate-spin rounded-full border-2 border-current border-r-transparent"
            />
          )}
          {ctaLabel}
        </button>
      </div>

      {consentText && (
        <label
          className={cn(
            "mt-2 flex items-start gap-2 text-xs leading-snug",
            inverse ? "text-on-surface-inverse-muted" : "text-fg-muted",
          )}
        >
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5"
          />
          <span>{consentText}</span>
        </label>
      )}

      <p
        id={errorId}
        role="alert"
        aria-live="polite"
        className={cn(
          "mt-2 min-h-[1.25rem] text-sm",
          clientError || subscribe.isError ? "text-danger" : "sr-only",
        )}
      >
        {message}
      </p>
    </form>
  );
}
